
<template>
  <div class="modal-container">
    <div
      class="modal-overlay"
      @click="close"
    />

    <div class="modal">
      <div class="modal-header">
        {{ $t('analyses.defineVariable') }}
      </div>

      <v-text-field
        class="formula"
        :label="$t('analyses.calculatedAnalysis')"
        :value="analysis.analysisType.formula"
        readonly
        outlined
      />

      <div class="table">
        <div class="table-header">
          <label class="variable-col">Variable</label>
          <label class="analysis-col">{{ $t('analyses.analysis') }}</label>
        </div>

        <div class="table-content">
          <div
            v-for="(variable, index) in variables"
            :key="index"
            class="table-row"
            :index="index"
            v-bind="variable"
          >
            <div
              v-if="variable.value"
              :class="config[index] && (config[index].network && config[index].component) ?
                'variable variable-col' : 'variable variable-col blocAlert'"
            >
              <div class="blocTitle">
                <div :class="config[index] && config[index].component ? 'variable-name' : 'variable-name alert'">
                  {{ variable.name }}
                </div>

                <div :class="config[index] && config[index].component ? 'variable-value' : 'variable-value alert'">
                  {{ variable.value.name[userLang] }} *
                </div>
              </div>
              <div
                v-if="config[index] && !config[index].component "
                class="textAlert"
              >
                {{ $t('analyses.sentenceAlert') }}
              </div>
            </div>

            <div
              v-else
              class="variable variable-col"
            >
              {{ $t('analyses.unconfigured') }}
            </div>

            <div class="analysis analysis-col">
              <v-treeview
                openAll
                :items="tree[variable.ref]"
                class="blocDefineVariable"
              >
                <template v-slot:label="{ item }">
                  <button
                    v-if="!item.children"
                    class="select-component"
                    :class="{
                      'is-selected': config[index] && item.id === config[index].component,
                    }"

                    @click="selectComponent(index, item)"
                  >
                    <span
                      v-if="config[index] && item.id === config[index].component"
                      class="material-icons"
                    >
                      done
                    </span>
                    {{ item.name }}
                  </button>

                  <button
                    v-else
                  >
                    {{ item.name }}
                  </button>
                </template>
              </v-treeview>
            </div>
          </div>
        </div>
      </div>

      <div class="actions">
        <button
          class="close-button"
          @click="close"
        >
          {{ $t('analyses.close') }}
        </button>

        <button
          class="confirm-button"
          :class="{ 'is-valid': isConfigValid && isVariablesConfigured }"
          @click="submit"
        >
          {{ $t('analyses.confirm') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

export default {
    name: 'DefineVariable',

    props: {
        analysis: {
            type: Object,
            default: () => ({
                analysisType: {
                    id: 'null',
                    formula: 'null',
                    name: {},
                    type: 'null',
                    variablesName: [],
                },
            }),
        },
    },

    data() {
        return {
            networksAndComponents: {},

            config: [],
        };
    },

    computed: {
        ...mapGetters([
            'userLang',
            'site',
            'hasPermission',
        ]),

        variables() {
            const result = this.analysis.analysisType.variablesName.length
                ? this.analysis.analysisType.variablesName : [{ name: 'A', value: null }];

            return result;
        },

        tree() {
            const tree = {};

            Object.keys(this.networksAndComponents).forEach(key => {
                if (!Array.isArray(tree[key])) tree[key] = [];
                this.networksAndComponents[key].forEach(item => {
                    const currentTree = tree[key];
                    // Check if network already recorded:
                    const networkIndex = currentTree.findIndex(_item => _item.id === item.network.id);

                    const child = {
                        id: item.component.id,
                        name: item.component.name,
                        network: item.network.id,
                        component: item.component.id,
                    };
                    // If it exists, add component the network's children:
                    if (networkIndex > -1) {
                        currentTree[networkIndex].children.push(child);
                        // Otherwise add new network entry:
                    } else {
                        currentTree.push({
                            id: item.network.id,
                            name: item.network.name,

                            network: item.network.id,

                            children: [child],
                        });
                    }
                });
            });

            return tree;
        },

        isConfigValid() {
            return this.config.every(item => item?.network && item?.component);
        },
        isVariablesConfigured() {
            return this.variables.every(item => item?.value);
        },
    },

    async created() {
        // Populate list of available networks and components:
        await this.getComponents();

        const savedVariables = this.analysis?.calculatedAnalysisParams?.variables;

        // Load saved config or default to a null value:
        this.config = this.variables.map((variable, index) => {
            const savedVariable = savedVariables ? savedVariables[index] : {};
            let component = null;
            let network = null;

            if (savedVariables && savedVariables[index]) {
                component = savedVariable.component;
                network = savedVariable.network;
            }

            return {
                name: variable.name,
                analysisType: variable.ref,
                network,
                component,
            };
        });
    },

    methods: {
        close() {
            this.$emit('close');
        },

        async getComponents() {
            const { data } = await this.$rq.getComponentsForAnalysisType(
                this.site.id,
                this.analysis.analysisType.id,
                this.analysis.component.id,
            );

            this.networksAndComponents = data;
        },

        selectComponent(index, component) {
            this.config[index] = { ...this.config[index], component: component.id, network: component.network };
            this.config = [...this.config];
        },

        async submit() {
            if (!this.isConfigValid) return;

            const response = await this.$rq.updateComponentsForAnalysisFormula(this.site.id, this.analysis.id, {
                formula: this.analysis.analysisType.formula,
                variables: this.config,
            });

            if (response.status === 200) {
                this.$emit('update', response.data);
                this.close();
            }
        },
    },
};
</script>

<style lang="scss" scoped>
.modal-container {
    position: fixed;
    top: 0;
    left: 0;

    display: flex;
    align-items: center;
    justify-content: center;

    width: 100%;
    height: 100%;
    z-index: 2;
}

.modal-overlay {
    position: fixed;

    top: 0;
    left: 0;

    width: 100%;
    height: 100%;

    z-index: -1;

    backdrop-filter: blur(1px);
    background: rgba(204,204,204,0.5);
}

.modal {
    position: absolute;

    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    max-width: 700px;
    max-height: calc(100vh - 100px) ;
    height: max-content;
    z-index: 3;

    box-shadow: rgba(0, 0, 0, 0.16) 0px 1px 4px;
    background: #FFFF;
    border-radius: 6px;
}

.modal-header {
    display: flex;

    margin: 0;
    width: 100%;

    padding: 15px 10px 5px 20px;

    font-size: 16px;
    color: #26384B;
    border-bottom: 1px solid rgba(141, 141, 141, 1);
}

.formula {
    width: 100%;
    max-width: 400px;
    margin-top: 20px;

    color: #26384B;
}

.formula label {
    font-size: 14px;
}

.formula input {
    color: #26384B;
}

.table {
    display: flex;
    flex-direction: column;

    width: 100%;
    max-width: 600px;
    margin-top: 10px;

    color: #26384B;
    font-size: 16px;
}

.table-header {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-around;

    width: 100%;
    padding: 8px 0px 0px 0px;

    font-weight: 500;

    background: #26384b;
    border-radius: 6px 6px 0px 0px;
}

.table-header label {
    color: #FFF;
    font-size: 14px;
    width: 100%;
}

.table-header label.variable-col {
    padding-left: 35px;
}

.table-content {
    display: flex;
    flex-direction: column;
    width: 100%;

    border: 1px solid #26384B;
    border-radius: 0 0 6px 6px;
}

.table-row {
    display: flex;

    width: 100%;
    border-top: 1px solid #26384B;
}

.table-content button {
    color: #26384B;
}

.variable-col {
    flex-shrink: 0;

    width: 100%;
    max-width: 220px;

}

.analysis-col {
    width: 100%;
    overflow: hidden;

    padding: 10px 0px 5px 20px;
}

.variable {
    display: flex;
    width: 100%;
    padding: 10px 0px 5px 20px;
    border-right: 1px solid #26384B;
}

.variable-value {
    margin-left: 20px;
}

.select-component.is-selected {
    padding: 10px 0px 5px 5px;
    display: flex;
    flex-direction: row;
    align-items: center
}

.actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: flex-end;

    padding: 20px;
    width: 100%;
}

.actions button {
    padding: 10px 15px;
    border-radius: 4px;
}

.close-button {
    color: #26384B;
}

.confirm-button {
    color: #FFFF;
    background: #26384B;

    opacity: 0.4;
    pointer-events: none;
}

.confirm-button.is-valid {
    opacity: 1.0;
    pointer-events: all;
}

.table {
  overflow-y: auto
}
.blocAlert {
  display: flex;
  flex-direction: column
}

.blocTitle {
  display: flex;
  flex-direction: row
}
.textAlert {
  color: red;
  font-size: 0.8rem
}
.alert {
  color: red
}
</style>

<style>
.analysis .v-treeview-node__level {
    width: 10px;
}

.analysis .v-treeview-node__root {
    min-height: auto;
    padding: 0;
}

</style>

<style lang="scss">
.blocDefineVariable .v-treeview-node__root:has(.is-selected) {
  background-color: #E7E7E7;
  width: 100%;
}
</style>
